import React, { useState, useEffect } from 'react';
import { 
  Calendar, Download, ExternalLink, Copy, Check, 
  Smartphone, Monitor, RefreshCw, Settings, Globe,
  CheckCircle, AlertCircle, Info 
} from 'lucide-react';
import { meetingService } from '../utils/supabaseData';
import { authService } from '../utils/supabase';
import { logger } from '../utils/logger';

const CalendarSyncSettings = ({ currentUser }) => {
  const [syncSettings, setSyncSettings] = useState({
    enableSync: false,
    syncFrequency: 'real-time',
    includeDetails: true,
    includePendingMeetings: true,
    includeCompletedMeetings: false,
    calendarName: 'RepConnect - Healthcare Meetings'
  });
  const [copiedUrl, setCopiedUrl] = useState('');
  const [lastSync, setLastSync] = useState(null);
  const [meetingStats, setMeetingStats] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (currentUser) {
        try {
          // Load meetings from Supabase to get stats
          const meetings = await meetingService.getAllMeetings(currentUser.id, currentUser.user_type);
          const stats = {
            total: meetings.length,
            upcoming: meetings.filter(m => new Date(m.date) > new Date() && m.status !== 'completed').length
          };
          setMeetingStats(stats);
        } catch (error) {
          logger.error('Error loading meeting stats:', error);
          setMeetingStats({ total: 0, upcoming: 0 });
        }
        
        // Load saved sync settings
        const savedSettings = localStorage.getItem(`calendar_sync_${currentUser.id}`);
        if (savedSettings) {
          setSyncSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
        }
        
        // Load last sync time
        const lastSyncTime = localStorage.getItem(`last_sync_${currentUser.id}`);
        if (lastSyncTime) {
          setLastSync(new Date(lastSyncTime));
        }
      }
    };

    loadData();
  }, [currentUser]);

  const generateCalendarUrls = () => {
    if (!currentUser) return {};
    const baseUrl = window.location.origin;
    const userId = currentUser.id;
    return {
      webcal: `webcal://${baseUrl.replace(/^https?:\/\//, '')}/calendar/${userId}/meetings.ics`,
      google: `https://calendar.google.com/calendar/r?cid=${baseUrl}/calendar/${userId}/meetings.ics`,
      outlook: `https://outlook.live.com/calendar/0/addfromweb?url=${baseUrl}/calendar/${userId}/meetings.ics`,
      dataUrl: `${baseUrl}/calendar/${userId}/meetings.ics`
    };
  };

  const handleCopyUrl = (url, type) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(type);
    setTimeout(() => setCopiedUrl(''), 2000);
  };

  const handleSyncSettingsChange = (setting, value) => {
    const newSettings = { ...syncSettings, [setting]: value };
    setSyncSettings(newSettings);
    
    if (currentUser) {
      localStorage.setItem(`calendar_sync_${currentUser.id}`, JSON.stringify(newSettings));
    }
  };

  const handleManualSync = () => {
    if (currentUser) {
      // Simulate sync process
      setLastSync(new Date());
      localStorage.setItem(`last_sync_${currentUser.id}`, new Date().toISOString());
      
      // In a real app, this would trigger actual calendar sync
      logger.debug('Manual sync triggered for user:', currentUser.id);
    }
  };

  const handleSubscribeToCalendar = () => {
    if (currentUser) {
      // Enable calendar subscription
      setSyncSettings({...syncSettings, enableSync: true});
      setLastSync(new Date());
      localStorage.setItem(`last_sync_${currentUser.id}`, new Date().toISOString());
      
      // Show success message
      alert('Calendar subscription enabled! Your meetings will now sync automatically.');
    }
  };

  const downloadFullCalendar = () => {
    if (currentUser) {
      // Create a simple ICS file for download
      const filename = `repconnect-meetings-${currentUser.id}.ics`;
      alert(`Calendar download would start: ${filename}`);
      // In a real implementation, this would generate and download an ICS file
    }
  };

  const urls = generateCalendarUrls();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Calendar Sync Settings</h2>
            <p className="text-gray-600 mb-4">
              Keep your external calendar synchronized with your RepConnect meetings. 
              Subscribe to your personal calendar feed or download meeting files.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-gray-600">Total Meetings: {meetingStats?.total || 0}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                <span className="text-gray-600">Upcoming: {meetingStats?.upcoming || 0}</span>
              </div>
              {lastSync && (
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Last sync: {lastSync.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={downloadFullCalendar}
          className="flex items-center justify-center space-x-2 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Download className="w-5 h-5 text-blue-600" />
          <span className="font-medium">Download Calendar</span>
        </button>
        
        <button
          onClick={handleManualSync}
          className="flex items-center justify-center space-x-2 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-5 h-5 text-green-600" />
          <span className="font-medium">Manual Sync</span>
        </button>
        
        <button
          onClick={handleSubscribeToCalendar}
          className={`flex items-center justify-center space-x-2 p-4 border rounded-lg transition-colors ${
            syncSettings.enableSync 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
          }`}
        >
          <Settings className="w-5 h-5" />
          <span className="font-medium">
            {syncSettings.enableSync ? 'Sync Enabled' : 'Subscribe to Calendar'}
          </span>
        </button>
      </div>

      {/* Calendar Subscription URLs */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Calendar Subscription</h3>
        <p className="text-gray-600 mb-6">
          Subscribe to your personal calendar feed to automatically sync meetings with your external calendar app.
        </p>

        <div className="space-y-4">
          {/* Universal Calendar URL */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Globe className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-800">Universal Calendar Feed</h4>
              </div>
              <button
                onClick={() => handleCopyUrl(urls.webcal, 'webcal')}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                {copiedUrl === 'webcal' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copiedUrl === 'webcal' ? 'Copied!' : 'Copy URL'}</span>
              </button>
            </div>
            <code className="block w-full p-3 bg-gray-50 rounded text-sm font-mono text-gray-700 break-all">
              {urls.webcal}
            </code>
            <p className="text-xs text-gray-500 mt-2">
              Works with most calendar applications including Apple Calendar, Outlook, and Google Calendar
            </p>
          </div>

          {/* Direct Integration Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href={urls.google}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Add to Google Calendar</span>
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </a>
            
            <a
              href={urls.outlook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Monitor className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Add to Outlook</span>
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </a>

            {urls.dataUrl && (
              <a
                href={urls.dataUrl}
                download={`meetingsync-calendar-${currentUser?.id}.ics`}
                className="flex items-center justify-center space-x-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Smartphone className="w-5 h-5 text-purple-600" />
                <span className="font-medium">Download Calendar</span>
                <Download className="w-4 h-4 text-gray-400" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Sync Settings */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Sync Preferences</h3>
        
        <div className="space-y-4">
          {/* Include Meeting Details */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-800">Include Meeting Details</h4>
              <p className="text-sm text-gray-600">Include restaurant, notes, and attendee information</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={syncSettings.includeDetails}
                onChange={(e) => handleSyncSettingsChange('includeDetails', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Include Pending Meetings */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-800">Include Pending Meetings</h4>
              <p className="text-sm text-gray-600">Show meetings awaiting confirmation</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={syncSettings.includePendingMeetings}
                onChange={(e) => handleSyncSettingsChange('includePendingMeetings', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Include Completed Meetings */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-800">Include Completed Meetings</h4>
              <p className="text-sm text-gray-600">Show past meetings for reference</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={syncSettings.includeCompletedMeetings}
                onChange={(e) => handleSyncSettingsChange('includeCompletedMeetings', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Calendar Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Calendar Name
            </label>
            <input
              type="text"
              value={syncSettings.calendarName}
              onChange={(e) => handleSyncSettingsChange('calendarName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter calendar name"
            />
            <p className="text-xs text-gray-500 mt-1">
              This name will appear in your external calendar application
            </p>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 mb-2">How Calendar Sync Works</h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p>• <strong>Real-time updates:</strong> Changes to meetings automatically sync to your calendar</p>
              <p>• <strong>Two-way sync:</strong> Updates made in your external calendar don't affect RepConnect data</p>
              <p>• <strong>Privacy:</strong> Only your meetings are included in your personal calendar feed</p>
              <p>• <strong>Compatibility:</strong> Works with Google Calendar, Outlook, Apple Calendar, and more</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarSyncSettings;